import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  APPS_DE_DELIVERY, CANAIS, formatPoints, platePoints, pointsLabel, resolveConfig,
  lerPalpite, type AppDeDelivery, type Canal, type ChallengeConfig,
} from "@nhomi/core";
import { ChaveDeConversa } from "@/ui/ChaveDeConversa";
import { useSession } from "@/lib/auth";
import { watchChallenge, type Challenge } from "@/data/challenges";
import { updatePlate, watchPlate, type Plate } from "@/data/plates";
import type { Coords } from "@/lib/places";
import { Empty, Spinner } from "@/ui/primitives";
import { descreverFalha, FalhaCard, type Falha } from "@/ui/Falha";
import { PlacePicker, type LugarEscolhido } from "@/ui/PlacePicker";
import { LogoDoApp } from "@/ui/LogoDoApp";
import {
  IconBack, IconCalendar, IconChevron, IconClock, IconPin,
} from "@/ui/icons";

/* ============================================================
   Editar prato

   Os mesmos campos de publicar, MENOS a foto. Trocar a foto de um prato
   já publicado significaria trocar a coisa que as pessoas notaram e
   comentaram — a nota fica pendurada num prato que virou outro. Quem
   errou a foto apaga e posta de novo, e aí a nota morre junto, que é o
   comportamento honesto.
   ============================================================ */

const hhmm = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function EditPlate() {
  const { cid = "", pid = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();

  const [plate, setPlate] = useState<Plate | null | undefined>(undefined);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [carregado, setCarregado] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [homemade, setHomemade] = useState(false);
  const [canal, setCanal] = useState<Canal | null>(null);
  const [appDelivery, setAppDelivery] = useState<AppDeDelivery | null>(null);
  const [conversa, setConversa] = useState(true);
  const [mealId, setMealId] = useState("");
  const [cuisineId, setCuisineId] = useState("");
  const [price, setPrice] = useState("");
  const [place, setPlace] = useState("");
  const [coords, setCoords] = useState<Coords | null>(null);
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [abrindoLocal, setAbrindoLocal] = useState(false);

  const [salvando, setSalvando] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);

  useEffect(() => watchPlate(cid, pid, setPlate), [cid, pid]);
  useEffect(() => watchChallenge(cid, setChallenge), [cid]);

  /* Preenche UMA vez. A escuta continua ativa, e reescrever o estado a
     cada snapshot apagaria o que a pessoa está digitando — inclusive por
     causa da própria escrita dela. */
  useEffect(() => {
    if (!plate || carregado) return;
    const at = plate.at?.toDate?.() ?? new Date();
    setTitle(plate.title);
    setDescription(plate.description ?? "");
    setHomemade(plate.homemade);
    setCanal(plate.canal ?? null);
    setAppDelivery(plate.appDelivery ?? null);
    setConversa(plate.fechado !== true);
    setMealId(plate.mealId ?? "");
    setCuisineId(plate.cuisineId ?? "");
    setPrice(plate.price != null ? String(plate.price) : "");
    setPlace(plate.place ?? "");
    setCoords(plate.coords);
    setDay(plate.day);
    setTime(hhmm(at));
    setCarregado(true);
  }, [plate, carregado]);

  const config: ChallengeConfig = useMemo(() => resolveConfig(challenge), [challenge]);

  if (plate === undefined) return <div className="screen center"><Spinner /></div>;
  if (!plate) {
    return <main className="screen"><Empty emoji="🍽️" title="Prato não encontrado" /></main>;
  }

  const meu = plate.uid === user?.uid;
  const dono = challenge?.ownerUid === user?.uid;

  if (!meu && !dono) {
    return (
      <main className="screen">
        <Topo cid={cid} pid={pid} />
        <Empty emoji="🔒" title="Esse prato não é seu">
          <p>Só quem postou edita — o dono do desafio só arruma data e lugar.</p>
        </Empty>
      </main>
    );
  }

  // O campo, como no placar: é ele que diz se o prato pagou por vir sem foto.
  const semFoto = plate.semFoto === true;
  const pts = (caseiro: boolean) =>
    platePoints(config, { homemade: caseiro, mealId: mealId || null, repeated: false })
    - (semFoto ? config.rules.semFotoCusto : 0);

  async function salvar() {
    // Prato sem foto é a descrição: apagá-la deixaria a IA e a galera sem nada.
    if (meu && semFoto && description.trim().length < 5) {
      setFalha({
        codigo: "sem-descricao", etapa: "salvar o prato",
        recado: "Este prato não tem foto: a descrição do que comeu é obrigatória.",
      });
      return;
    }
    const precoLido = meu && price.trim() ? lerPalpite("price", price) : null;
    if (precoLido && !precoLido.ok) {
      setFalha({ codigo: "preco", etapa: "salvar o prato", recado: precoLido.erro });
      return;
    }
    setSalvando(true);
    setFalha(null);
    try {
      const at = new Date(`${day}T${time || "12:00"}:00`);
      // O dono do desafio só pode mexer em data e lugar: é o que as regras
      // do Firestore permitem, e a tela precisa combinar com elas — senão
      // o botão promete uma coisa e o banco recusa.
      await updatePlate(cid, pid, meu
        ? {
            title: title.trim() || plate!.title,
            description: description.trim(),
            mealId: mealId || null,
            cuisineId: cuisineId || null,
            homemade,
            // Prato antigo sem o campo continua sem ele: só grava se escolheu.
            ...(homemade ? {} : canal || plate!.canal
              ? { canal, appDelivery: canal === "delivery" ? appDelivery : null }
              : {}),
            price: precoLido?.ok ? precoLido.valor : null,
            place: place.trim(),
            coords,
            at,
            // Só grava se mudou: prato antigo sem o campo continua sem ele.
            ...(conversa === (plate!.fechado !== true) ? {} : { fechado: !conversa }),
          }
        : { place: place.trim(), coords, at });
      navigate(`/c/${cid}/p/${pid}`);
    } catch (err) {
      setSalvando(false);
      setFalha(descreverFalha(err, "salvar o prato"));
    }
  }

  const fecharLocal = (v: LugarEscolhido | null) => {
    setAbrindoLocal(false);
    if (!v) return;
    setPlace(v.name);
    setCoords(v.coords);
  };

  const soZeladoria = !meu && dono;

  return (
    <main className="screen">
      <Topo cid={cid} pid={pid} acao={
        <button className="topbar-action" onClick={salvar} disabled={salvando}>
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      } />

      <div className="screen-body no-tabbar">
        {falha && <FalhaCard falha={falha} />}

        {soZeladoria && (
          <div className="hint-row">
            Você é o dono do desafio: dá pra arrumar a data e o lugar deste
            prato, mas não o conteúdo dele.
          </div>
        )}

        {!soZeladoria && (
          <>
            <div className="card">
              <label className="field">
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="Título" maxLength={80} autoComplete="off" />
              </label>
              <label className="field">
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder={semFoto ? "O que você comeu e quanto (obrigatório)" : "Descrição (opcional)"}
                  maxLength={600} />
              </label>
            </div>

            <div className="card">
              <div className="toggle-row">
                <button className={`toggle-opt ${homemade ? "" : "active"}`}
                  onClick={() => setHomemade(false)}>
                  🛒 Comprei<span>{pointsLabel(pts(false))}</span>
                </button>
                <button className={`toggle-opt ${homemade ? "active" : ""}`}
                  onClick={() => setHomemade(true)}>
                  👨‍🍳 Cozinhei<span>{pointsLabel(pts(true))}</span>
                </button>
              </div>
              <div className="hint-row">
                Mudar isto refaz o seu placar — a função recalcula sozinha.
              </div>
              {!homemade && (
                <>
                  <div className="field-label chips-titulo canal-titulo">
                    Como foi? <span className="opcional">opcional</span>
                  </div>
                  <div className="chip-wrap">
                    {CANAIS.map((c) => (
                      <button key={c.id} className={`chip ${canal === c.id ? "active" : ""}`}
                        onClick={() => {
                          setCanal(canal === c.id ? null : c.id);
                          if (c.id !== "delivery" || canal === c.id) setAppDelivery(null);
                        }}>
                        {c.emoji} {c.rotulo}
                      </button>
                    ))}
                  </div>
                  {canal === "delivery" && (
                    <div className="chip-wrap canal-apps">
                      {APPS_DE_DELIVERY.map((a) => (
                        <button key={a.id} className={`chip ${appDelivery === a.id ? "active" : ""}`}
                          onClick={() => setAppDelivery(appDelivery === a.id ? null : a.id)}>
                          <LogoDoApp app={a.id} /> {a.rotulo}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {!soZeladoria && <ChaveDeConversa ligado={conversa} aoMudar={setConversa} />}

        <div className="card">
          <button className="field-inline place-btn" onClick={() => setAbrindoLocal(true)}>
            <span className="ico"><IconPin size={22} /></span>
            <span className="field-body">
              <span className={`place-name ${place ? "filled" : ""}`}>
                {place || "Onde foi? (opcional)"}
              </span>
              {coords && <span className="place-sub">📍 marcado no mapa</span>}
            </span>
            <span className="chev"><IconChevron size={18} /></span>
          </button>
        </div>

        {!soZeladoria && (
          <div className="card">
            <div className="field-inline">
              <span className="ico emoji">💸</span>
              <div className="field-body">
                <span className="field-label">Quanto custou? (opcional)</span>
                <input type="number" inputMode="decimal" min="0" max="5000" step="0.01" placeholder="R$ —"
                  value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
            </div>
            <div className="hint-row">
              Mudar o preço depois de a galera chutar não apaga os chutes —
              quem cravou o valor antigo mantém o ponto.
            </div>
          </div>
        )}

        <div className="row-2">
          <div className="card"><div className="field-inline">
            <div className="field-body">
              <span className="field-label">Dia</span>
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </div>
            <span className="ico"><IconCalendar size={22} /></span>
          </div></div>
          <div className="card"><div className="field-inline">
            <div className="field-body">
              <span className="field-label">Hora</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            </div>
            <span className="ico"><IconClock size={22} /></span>
          </div></div>
        </div>
        {day !== plate.day && (
          <div className="hint-row">
            Mudando de {plate.day} para {day || "—"}. O dia decide a sequência,
            então isso pode mexer no bônus.
          </div>
        )}

        {!soZeladoria && (
          <>
            <div className="card">
              <div className="field-label chips-titulo">Refeição</div>
              <div className="chip-wrap">
                {config.meals.map((m) => (
                  <button key={m.id} className={`chip ${m.id === mealId ? "active" : ""}`}
                    onClick={() => setMealId(m.id === mealId ? "" : m.id)}>
                    {m.emoji || "🍽️"} {m.label}
                    {m.weight !== 1 && <span className="chip-peso">×{formatPoints(m.weight)}</span>}
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="field-label chips-titulo">Selo · carimba o passaporte</div>
              <div className="chip-wrap scroll">
                {config.cuisines.map((c) => (
                  <button key={c.id} className={`chip ${c.id === cuisineId ? "active" : ""}`}
                    onClick={() => setCuisineId(c.id === cuisineId ? "" : c.id)}>
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <div className="hint-row">
          {semFoto && "Este prato foi postado sem foto, e continua sem: foto não se põe depois. "}
          A foto não se troca. Ela é a coisa que a galera notou e comentou;
          trocar deixaria a nota pendurada num prato que virou outro. Errou a
          foto? Apaga e posta de novo.
        </div>

        <div className="gap" />
      </div>

      {abrindoLocal && (
        <PlacePicker inicial={{ name: place, coords }} onFechar={fecharLocal} />
      )}
    </main>
  );
}

function Topo({ cid, pid, acao }: { cid: string; pid: string; acao?: React.ReactNode }) {
  return (
    <div className="topbar">
      <Link className="topbar-btn" to={`/c/${cid}/p/${pid}`} aria-label="Voltar"><IconBack /></Link>
      <div className="topbar-title">Editar prato</div>
      {acao ?? <span className="topbar-btn" />}
    </div>
  );
}
